// frontend/src/components/MetaCubeOracleV4.jsx
import React, { useRef, useState, useEffect } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";

/**
 * Core MetaCube Oracle V4
 * - Cube central multi-faces
 * - Halo énergétique
 * - 3 surfaces flottantes
 */
function MetaCubeOracleV4Core({
  running = true,
  chaos = false,
  glow = true,
  cubeSpeedPct = 100,
  ringSpeedPct = 100,
  colorShiftRatePct = 50,
  bloomIntensity = 1.3,
  bloomRadius = 0.7,
  bloomThreshold = 0.0,
  onEntropyUpdate = () => {},
}) {
  const groupRef = useRef();
  const [time, setTime] = useState(0);
  const [entropy, setEntropy] = useState({ shannon: 0, accumulator: 0 });

  // --- Fetch entropie dynamique ---
  useEffect(() => {
    let active = true;
    const fetchEntropy = async () => {
      try {
        // ⚠️ LIT LA MESURE, PLUS UN CHAMP ALÉATOIRE DE /animate.
        //
        // Le canal dynamique affiché venait de `/animate`, dont les champs
        // `shannon_entropy` et `entropy_accumulator` sont des tirages — et le
        // backend a cessé d'en écrire la métrique le 2026-07-29. La page
        // montrait donc un nombre qui n'était mesuré nulle part.
        //
        // Elle lit désormais `/dyn_entropy`, qui mesure la scène canonique.
        //
        // Ce module était le cas le plus net : `/animate` posait
        // `shannon_val = random.uniform(4.0, 6.0)` et l'écrivait dans
        // l'agrégat sous le nom d'entropie de Shannon.
        const res = await fetch("/api/geometry/metacube_oracle_v4/dyn_entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        });
        const data = await res.json();
        if (active && typeof data.entropy_dyn === "number") {
          const val = {
            shannon: data.entropy_dyn,
            accumulator: data.entropy_dyn,
          };
          setEntropy(val);
          onEntropyUpdate(val);
        }
      } catch (err) {
        console.warn("MetaCubeOracleV4 backend error:", err);
      }
    };
    if (running) {
      fetchEntropy();
      const id = setInterval(fetchEntropy, 2000);
      return () => {
        active = false;
        clearInterval(id);
      };
    }
  }, [running, onEntropyUpdate]);

  // --- Animation ---
  useFrame((_, delta) => {
    if (!running || !groupRef.current) return;
    setTime((t) => t + delta);

    const g = groupRef.current;

    // Rotation cube central
    const cube = g.getObjectByName("metacube-v4");
    if (cube) {
      const speedFactor = cubeSpeedPct / 10;
      cube.rotation.x += delta * 2 * speedFactor;
      cube.rotation.y += delta * 5 * speedFactor;
    }

    // Halo pulse
    const halo = g.getObjectByName("halo-v4");
    if (halo) {
      const shiftFactor = colorShiftRatePct / 100;
      const scale =
        1 + Math.sin(time * (5 + shiftFactor)) * 0.08 + entropy.shannon * 0.05;
      halo.scale.setScalar(scale);
    }

    // Chaos mode
    if (chaos) {
      g.position.x = Math.sin(time * 2) * 0.7;
      g.position.y = Math.cos(time * 2.3) * 0.7;
    } else {
      g.position.set(0, 0, 0);
    }
  });

  return (
    <>
      <group ref={groupRef}>
        {/* Cube central */}
        <mesh name="metacube-v4">
          <boxGeometry args={[14, 14, 14]} />
          <meshPhongMaterial
            color={new THREE.Color().setHSL(0.6, 0.7, 0.5)}
            emissive={glow ? new THREE.Color(0x33ccff) : new THREE.Color(0x000000)}
            shininess={180}
            transparent
            opacity={0.95}
          />
        </mesh>

        {/* Halo */}
        <mesh name="halo-v4">
          <sphereGeometry args={[18, 64, 64]} />
          <meshBasicMaterial
            color={0x00ffff}
            transparent
            opacity={0.25}
            side={THREE.BackSide}
          />
        </mesh>

        {/* Surfaces flottantes */}
        {[
          { pos: [25, 0, 0], rot: [0, Math.PI / 2, 0], color: 0xffa500 },
          { pos: [-25, 0, 0], rot: [0, -Math.PI / 2, 0], color: 0x9400d3 },
          { pos: [0, 25, 0], rot: [Math.PI / 2, 0, 0], color: 0x32cd32 },
        ].map((cfg, i) => (
          <mesh key={i} position={cfg.pos} rotation={cfg.rot}>
            <planeGeometry args={[18, 18]} />
            <meshPhongMaterial
              color={cfg.color}
              emissive={new THREE.Color(cfg.color).multiplyScalar(glow ? 0.4 : 0)}
              transparent
              opacity={0.85}
              side={THREE.DoubleSide}
            />
          </mesh>
        ))}
      </group>

      {/* Effets post-processing */}
      <EffectComposer>
        <Bloom
          intensity={bloomIntensity}
          radius={bloomRadius}
          threshold={bloomThreshold}
        />
      </EffectComposer>
    </>
  );
}

/**
 * Wrapper principal → Canvas unique avec classe CSS
 */
export default function MetaCubeOracleV4(props) {
  return (
    <div id="metacube-oracle-v4-3d">
      <Canvas camera={{ position: [0, 20, 50], fov: 60 }}>
        <ambientLight intensity={0.6} />
        <pointLight position={[20, 20, 20]} intensity={1.3} />
        <MetaCubeOracleV4Core {...props} />
        <OrbitControls enableZoom enablePan />
      </Canvas>
    </div>
  );
}
